import { inject, Injectable } from '@angular/core';
import { UtilsService } from 'src/app/edge/utils.service';
import { ContextualLoggerService } from 'src/app/services/contextual-logger.service';
import { LoggerService } from 'src/app/services/logger.service';
import { WorkflowWebhooksService } from 'src/app/services/workflow-webhooks.service';
import { catchError, share, switchMap } from 'rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { WorkflowWebhookActions } from '@states/workflow-webhooks/workflow-webhooks.action-type';
import { SharedActions } from '@states/shared/shared.action-types';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class WorkflowWebhooksEffect {
  private baseLogger = inject(LoggerService);
  private context = 'WorkflowWebhooksEffect';
  private logger = new ContextualLoggerService(this.baseLogger, this.context);

  constructor(
    private actions$: Actions,
    private utilsService: UtilsService,
    private translateService: TranslateService,
    private workflowWebhooksService: WorkflowWebhooksService,
  ) { }

  public getWorkflowWebhooks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowWebhookActions.getWorkflowWebhooks),
      switchMap(({ orgId }) => {
        return [
          WorkflowWebhookActions.setIsLoading({ isLoading: true }),
          WorkflowWebhookActions.getWorkflowWebhooksServerCall({ orgId }),
        ];
      }),
    ),
  );

  public getWorkflowWebhooksServerCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowWebhookActions.getWorkflowWebhooksServerCall),
      switchMap(({ orgId }) => {
        return this.workflowWebhooksService.getWebhooks(orgId).pipe(
          switchMap((webhooks) => {
            return [
              WorkflowWebhookActions.getWorkflowWebhooksSuccess({
                webhooks: webhooks,
              }),
              WorkflowWebhookActions.setIsLoading({ isLoading: false }),
            ];
          }),
          catchError((response) => {
            const errorMessage = this.utilsService.errMessage(response);
            this.logger.error(errorMessage);
            return [
              WorkflowWebhookActions.setIsLoading({ isLoading: false }),
              WorkflowWebhookActions.getWorkflowWebhookshFail(),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public deleteWorkflowWebhook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowWebhookActions.deleteWorkflowWebhook),
      switchMap(({ id }) => {
        return [WorkflowWebhookActions.deleteWorkflowWebhookServerCall({ id })];
      }),
    ),
  );

  public deleteWorkflowWebhookServerCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowWebhookActions.deleteWorkflowWebhookServerCall),
      switchMap(({ id }) => {
        return this.workflowWebhooksService.deleteWebhook(id).pipe(
          switchMap(() => {
            return [
              SharedActions.showMessage({ success: this.translateService.instant('WORKFLOW_WEBHOOK.TOASTS.DELETED_SUCCESS') }),
              WorkflowWebhookActions.deleteWorkflowWebhookSuccess({ id }),
            ];
          }),
          catchError((response) => {
            return [
              this.catchError(response),
              WorkflowWebhookActions.deleteWorkflowWebhookhFail(),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public createWorkflowWebhook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowWebhookActions.createWorkflowWebhook),
      switchMap(({ orgId, webhook }) => {
        return [
          WorkflowWebhookActions.setIsSaving({ isSaving: true }),
          WorkflowWebhookActions.createWorkflowWebhookServerCall({
            orgId,
            webhook,
          }),
        ];
      }),
    ),
  );

  public createWorkflowWebhookServerCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowWebhookActions.createWorkflowWebhookServerCall),
      switchMap(({ orgId, webhook }) => {
        return this.workflowWebhooksService.createWebhook(orgId, webhook).pipe(
          switchMap((webhook) => {
            return [
              SharedActions.showMessage({ success: this.translateService.instant('WORKFLOW_WEBHOOK.TOASTS.CREATED_SUCCESS') }),
              WorkflowWebhookActions.createWorkflowWebhookSuccess({
                webhook: webhook,
              }),
              WorkflowWebhookActions.setIsSaving({ isSaving: false }),
            ];
          }),
          catchError((response) => {
            return [
              this.catchError(response),
              WorkflowWebhookActions.setIsSaving({ isSaving: false }),
              WorkflowWebhookActions.createWorkflowWebhookhFail(),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  public editWorkflowWebhook$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowWebhookActions.editWorkflowWebhook),
      switchMap(({ id, webhook }) => {
        return [
          WorkflowWebhookActions.setIsSaving({ isSaving: true }),
          WorkflowWebhookActions.editWorkflowWebhookServerCall({
            id,
            webhook,
          }),
        ];
      }),
    ),
  );

  public editWorkflowWebhookServerCall$ = createEffect(() =>
    this.actions$.pipe(
      ofType(WorkflowWebhookActions.editWorkflowWebhookServerCall),
      switchMap(({ id, webhook }) => {
        return this.workflowWebhooksService.editWebhook(id, webhook).pipe(
          switchMap((webhook) => {
            return [
              SharedActions.showMessage({ success: this.translateService.instant('WORKFLOW_WEBHOOK.TOASTS.UPDATED_SUCCESS') }),
              WorkflowWebhookActions.editWorkflowWebhookSuccess({
                webhook: webhook,
              }),
              WorkflowWebhookActions.setIsSaving({ isSaving: false }),
            ];
          }),
          catchError((response) => {
            return [
              this.catchError(response),
              WorkflowWebhookActions.setIsSaving({ isSaving: false }),
              WorkflowWebhookActions.editWorkflowWebhookhFail(),
            ];
          }),
        );
      }),
      share(),
    ),
  );

  private catchError(response) {
    const errorMessage = this.utilsService.errMessage(response);
    return SharedActions.showMessage({ error: errorMessage });
  }
}
